import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { YearPicker, Loader } from 'qampo-ui-library';
import ROUTES from '../routes/routeConstants';
import { OverviewLocationCard } from '../components/Overview/OverviewLocationCard';
import { LoadError } from '../components/UI';
import { useLocations } from '../hooks/useLocations';
import { useOverviewTargets } from '../hooks/useOverviewTargets';
import { useSelectedYear } from '../hooks/useSelectedYear';
import { resolveLocationColorId } from '../services/locationColorService';
import type { OverviewLocation } from '../types/overviewTypes';
import { useAppStore } from '../store/hooks';
import {
  selectLocationsIsLoading,
  selectLocationsLoadError
} from '../store/locations/locationsSlice';
import styles from './OverviewPage.module.css';

/**
 * The landing page: every location the planner can read, for one year, against
 * that year's target.
 *
 * Replaces the plain locations list. It carries a year selector and no location
 * selector, because it already shows every location, and because a target is a
 * per-year value: "this is a bad place to have these because you will need a
 * year picker ... it makes so much more sense to make a statistics page"
 * (Steffen, 2026-08-18).
 */
export const OverviewPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const store = useAppStore();
  const { locations, isLoading: isLoadingLocations, loadError } = useLocations();
  const { selectedYear, setSelectedYear } = useSelectedYear();

  const locationIds = useMemo(() => locations.map((location) => location.id), [locations]);
  const {
    data,
    isLoading,
    loadError: targetsLoadError,
    refresh: refreshTargets
  } = useOverviewTargets(locationIds, selectedYear);

  const dataByLocationId = useMemo(() => {
    const map: Record<string, OverviewLocation> = {};
    for (const entry of data?.locations ?? []) {
      map[entry.locationId] = entry;
    }
    return map;
  }, [data]);

  // Same guard as the locations list it replaces: without the location list the
  // page cannot show anything, so a settled failure goes to the error page. Only
  // a SETTLED failure counts, since useLocations may already have started a
  // retry that cleared the error this render captured.
  useEffect(() => {
    if (!loadError) return;
    const state = store.getState();
    if (selectLocationsIsLoading(state) || selectLocationsLoadError(state) === null) return;
    navigate(`/${ROUTES.errorPage}`);
  }, [loadError, navigate, store]);

  return (
    <main className={styles.overview}>
      <div className={styles.titleRow}>
        <h1 className={styles.heading}>{t('overview-title')}</h1>
        <div className={styles.yearPicker}>
          <YearPicker
            value={selectedYear}
            onChange={setSelectedYear}
            classes={styles.yearValue}
            theme={{
              inputBackground: 'transparent',
              popupBackground: 'var(--color-surface-primary)',
              yearSelectedBackground: 'var(--color-interaction-primary)',
              yearSelectedBorderRadius: 'var(--radius-regular)'
            }}
          />
        </div>
      </div>

      {/* The shared component rather than a bare paragraph: it carries role="alert"
          so the failure is announced, and a retry, since the cards stay on screen
          behind it saying they have no figures. */}
      {targetsLoadError && <LoadError message={targetsLoadError} onRetry={refreshTargets} />}
      {isLoading || isLoadingLocations ? (
        <div className={styles.loading}>
          <Loader />
        </div>
      ) : (
        <div className={styles.cardList}>
            {locations.map((location) => (
              <OverviewLocationCard
                key={location.id}
                locationId={location.id}
                name={location.name}
                colorId={resolveLocationColorId(location)}
                year={selectedYear}
                location={dataByLocationId[location.id]}
              />
            ))}
        </div>
      )}
    </main>
  );
};
